import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useSocial } from './social-context';

/**
 * Sintaxis de mención: `@[Nombre](tipo:id)`.
 * La convertimos a un enlace markdown `[@Nombre](#m:tipo:id)` y lo renderizamos
 * como un chip interactivo (hover con preview + click al perfil).
 */
const MENTION_RE = /@\[([^\]]+)\]\((hermandad|banda|organizacion):([^)]+)\)/g;

export function contentToMarkdown(content: string): string {
    if (!content) return '';
    return content.replace(MENTION_RE, (_m, name, type, id) => `[@${name}](#m:${type}:${id})`);
}

function MentionChip({ name, type, id }: { name: string; type: 'hermandad' | 'banda' | 'organizacion'; id: string }) {
    const { profiles, onMention } = useSocial();
    const [hovered, setHovered] = useState(false);
    const profile = profiles.get(`${type}:${id}`);
    const displayName = profile?.name || name;

    return (
        <span className="relative inline-block align-baseline">
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMention(type, id); }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                className="font-semibold text-primary hover:underline"
            >
                @{displayName}
            </button>
            {hovered && (
                <span
                    className="absolute bottom-full left-0 z-50 mb-1.5 flex items-center gap-2 whitespace-nowrap rounded-xl border border-white/10 bg-popover/95 px-3 py-2 shadow-xl backdrop-blur-md"
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMention(type, id); }}
                >
                    <Avatar className="h-8 w-8 border border-white/10">
                        <AvatarImage src={profile?.escudoUrl} />
                        <AvatarFallback>{displayName.substring(0, 2)}</AvatarFallback>
                    </Avatar>
                    <span className="flex flex-col">
                        <span className="text-sm font-semibold leading-tight text-foreground">{displayName}</span>
                        <span className="text-[0.65rem] capitalize text-muted-foreground">{type}</span>
                    </span>
                </span>
            )}
        </span>
    );
}

export function SocialText({ content, className }: { content: string; className?: string }) {
    const md = contentToMarkdown(content);
    return (
        <div className={cn('prose prose-sm prose-invert max-w-none leading-relaxed', className)}>
            <ReactMarkdown
                remarkPlugins={[remarkBreaks]}
                components={{
                    a: ({ href, children }) => {
                        if (href && href.startsWith('#m:')) {
                            const [, type, id] = href.split(':');
                            const name = String(children).replace(/^@/, '');
                            return <MentionChip name={name} type={type as any} id={id} />;
                        }
                        return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                    },
                }}
            >
                {md}
            </ReactMarkdown>
        </div>
    );
}
